import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { SITE_NAME } from '../data/toolRegistry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center p-6 text-stone-900 font-sans">
          <div className="max-w-[480px] w-full bg-white rounded-3xl p-8 border border-stone-200 shadow-sm text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8" />
            </div>
            
            <h1 className="text-2xl font-bold tracking-tight mb-3">Something went wrong</h1>
            <p className="text-[15px] text-stone-500 mb-8 leading-relaxed">
              An unexpected error occurred in {SITE_NAME}. This might be due to a corrupted local data state or a temporary glitch.
            </p>

            <div className="flex flex-col gap-3">
              <button 
                onClick={this.handleReload}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-stone-900 text-white rounded-xl text-[14px] font-semibold hover:bg-stone-800 transition-colors"
              >
                <RefreshCcw className="h-4 w-4" />
                Reload Page
              </button>
              <button 
                onClick={this.handleGoHome}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-stone-700 border border-stone-200 rounded-xl text-[14px] font-semibold hover:bg-stone-50 transition-colors"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
